import type { CommandBus } from '../bridge/CommandBus';
import type { InputController } from '../input/InputController';
import {
  BALANCE,
  BUILDING_DEFS,
  CABLE_COST_CENTS,
  PAVED_COST_CENTS,
  PIPE_COST_CENTS,
  RECLAIM_COST_CENTS_PER_LEVEL,
  ROAD_COST_CENTS,
} from '../sim/constants';
import { clearSave } from '../sim/save';
import type { Simulation } from '../sim/sim';
import { MUSIC_TRACKS } from '../audio/music';
import { buyoutPriceCents, stripSettlementCents } from '../sim/systems/endgame';
import { familySettlementReady, vacantFamilyCapacity } from '../sim/systems/families';
import { outputCapUnits, productionStall } from '../sim/systems/industry';
import { letterDef } from '../sim/systems/letters';
import { showSettingsModal } from './SettingsModal';
import { showEpilogue } from './Epilogue';
import type { PayrollBreakdown, SimEvent, Speed } from '../sim/types';
import type { HudSnapshot } from '../sim/view';
import { el, formatMoney, formatTons } from './dom';
import type { IconFactory } from './icons';
import { Sidebar } from './Sidebar';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Top bar, tool hint, hover inspector, payroll modal, game-over overlay. */
export class Hud {
  private readonly view: Simulation;
  private readonly input: InputController;
  private readonly bus: CommandBus;
  private readonly root: HTMLElement;
  private readonly cash: HTMLElement;
  private readonly date: HTMLElement;
  private readonly crew: HTMLElement;
  private readonly depot: HTMLElement;
  private readonly market: HTMLElement;
  private readonly water: HTMLElement;
  private readonly sellButton: HTMLButtonElement;
  private readonly speedButtons = new Map<Speed, HTMLButtonElement>();
  private readonly toolChip: HTMLElement;
  private readonly inspectBox: HTMLElement;
  private readonly sidebar: Sidebar;
  private crewCollapsed = false;
  /** paidIdle flickers for a few ticks between job assignments — damp the
   * readout to 1Hz so the panel doesn't rebuild in a blur. UI-only. */
  private paidIdleDisplay = 0;
  private paidIdleUpdatedMs = 0;
  private modal: HTMLElement | null = null;
  private gameOverShown = false;
  private lastHudText = '';
  private crewPanel!: HTMLElement;
  private selectionPanel!: HTMLElement;
  private lastCrewKey = '';
  private lastSelectionKey = '';
  private marketChip!: HTMLButtonElement;
  private marketPanel!: HTMLElement;
  private marketOpen = false;
  private lastMarketKey = '';
  private quotaStat!: HTMLElement;
  private mailButton!: HTMLButtonElement;
  private newGameButton!: HTMLButtonElement;
  private toastStack!: HTMLElement;
  private mailOpen = false;
  private lastChipKey = '';
  private townStat!: HTMLElement;
  private charterButton!: HTMLButtonElement;
  private pulloutButton!: HTMLButtonElement;
  private readonly snapshot: (() => HTMLCanvasElement | null) | null;
  private epilogueShown = false;
  // Auto-opening mail: delivered letters, family requests, and the pull-out
  // ultimatum queue here and open one at a time, pausing the game while read.
  private mailQueue: (
    | { kind: 'letter'; defId: string }
    | { kind: 'family'; requestId: number }
    | { kind: 'pullout' }
  )[] = [];
  private queuePriorSpeed: Speed | null = null; // non-null = pause session active
  /** Set by the tutorial: deliver letters to Mail without popping them open. */
  suppressAutoOpenLetters = false;

  constructor(
    root: HTMLElement,
    view: Simulation,
    input: InputController,
    bus: CommandBus,
    icons: IconFactory,
    gameCanvas: HTMLCanvasElement,
    snapshot: (() => HTMLCanvasElement | null) | null = null,
  ) {
    this.view = view;
    this.input = input;
    this.bus = bus;
    this.root = root;
    this.snapshot = snapshot;

    this.townStat = el('span', { class: 'stat town-name' });
    // One button, two moods: locked it spells out what the Land Office still
    // wants; ready it pulses and opens the charter itself.
    this.charterButton = el(
      'button',
      {
        class: 'sell charter locked',
        onclick: () =>
          this.view.hud().charterReady ? this.showCharterModal() : this.showCharterRequirementsModal(),
      },
      'Sign Charter',
    );
    this.charterButton.style.display = 'none';
    this.pulloutButton = el('button', { class: 'sell charter', onclick: () => this.showPulloutModal() }, 'HQ Ultimatum');
    this.pulloutButton.style.display = 'none';
    this.cash = el('span', { class: 'stat clickable', title: 'Open the books' });
    // The debt chip is re-rendered by innerHTML each frame — delegate.
    this.cash.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('.debt-chip')) this.showDebtModal();
      else this.showBooks();
    });
    this.date = el('span', { class: 'stat' });
    this.crew = el('span', {
      class: 'stat',
      title:
        'Employed / employable adults. Employed = waged crew + amenity staff; every adult can take a job. Kids excluded.',
    });
    this.depot = el('span', { class: 'stat' });
    this.market = el('span', { class: 'stat' });
    this.water = el('span', { class: 'stat' });
    this.quotaStat = el('span', { class: 'stat' });
    this.sellButton = el(
      'button',
      { class: 'sell', 'data-tutorial': 'sell', onclick: () => bus.dispatch({ kind: 'dispatchTruck' }) },
      'Sell ore',
    );
    this.marketChip = el(
      'button',
      {
        class: 'sell',
        title: 'Depot stock & market prices',
        onclick: () => {
          this.marketOpen = !this.marketOpen;
          this.lastMarketKey = '';
        },
      },
      'Market ▾',
    );
    this.marketChip.style.display = 'none';
    this.mailButton = el('button', { class: 'sell', 'data-tutorial': 'mail', onclick: () => this.toggleMail() }, 'Mail');
    this.newGameButton = el('button', { class: 'sell', onclick: () => this.confirmNewGame() }, 'New');

    const speeds: [Speed, string][] = [
      [0, 'II'],
      [1, '1x'],
      [3, '3x'],
      [8, '8x'],
    ];
    const speedGroup = el('div', { class: 'speed-group' });
    for (const [speed, label] of speeds) {
      const button = el(
        'button',
        { 'data-tutorial': `speed-${speed}`, onclick: () => bus.dispatch({ kind: 'setSpeed', speed }) },
        label,
      );
      this.speedButtons.set(speed, button);
      speedGroup.append(button);
    }

    // Persistent corner icons: music credits (top left), settings cog
    // (top right).
    const iconButton = (glyph: Parameters<IconFactory['glyph']>[0], title: string, onclick: () => void): HTMLButtonElement => {
      const canvas = icons.glyph(glyph);
      return el('button', { class: 'icon-button', title, onclick }, canvas);
    };
    const creditsButton = iconButton('paper', 'Music credits', () => this.showCredits());
    const settingsButton = iconButton('cog', 'Settings', () => {
      this.closeModal();
      showSettingsModal(this.root);
    });

    const topbar = el(
      'div',
      { class: 'panel topbar' },
      creditsButton,
      this.townStat,
      this.cash,
      this.date,
      this.crew,
      this.depot,
      this.market,
      this.marketChip,
      this.water,
      this.quotaStat,
      this.sellButton,
      this.mailButton,
      this.charterButton,
      this.pulloutButton,
      this.newGameButton,
      speedGroup,
      settingsButton,
    );
    this.toastStack = el('div', { class: 'toasts' });
    root.append(this.toastStack);

    this.toolChip = el('div', { class: 'panel toolchip' });
    this.toolChip.style.display = 'none';
    this.inspectBox = el('div', { class: 'panel inspectbox' });
    this.crewPanel = el('div', { class: 'panel crew' });
    this.selectionPanel = el('div', { class: 'panel selection' });
    this.selectionPanel.style.display = 'none';
    this.marketPanel = el('div', { class: 'panel market-panel' });
    this.marketPanel.style.display = 'none';
    root.append(topbar, this.toolChip, this.inspectBox, this.crewPanel, this.selectionPanel, this.marketPanel);
    this.sidebar = new Sidebar(root, input, view, icons, gameCanvas);
    input.onToolChange = () => {
      this.renderToolChip();
      this.sidebar.syncActive();
    };
    this.renderToolChip();
  }

  handleEvents(events: readonly SimEvent[]): void {
    for (const event of events) {
      if (event.kind === 'payroll') this.showPayroll(event.breakdown);
      else if (event.kind === 'toast') this.showToast(event.message);
      else if (event.kind === 'letterDelivered') {
        // The tutorial owns the screen while it runs: letters land in Mail
        // with a toast instead of a modal (payroll still opens — that's a
        // tutorial lesson itself).
        const def = letterDef(event.defId);
        if (def?.autoOpen !== false) {
          if (this.suppressAutoOpenLetters) {
            this.showToast(`Mail: ${def?.subject ?? 'A letter from HQ'}`);
          } else {
            this.mailQueue.push({ kind: 'letter', defId: event.defId });
          }
        }
      } else if (event.kind === 'familyRequest') this.mailQueue.push({ kind: 'family', requestId: event.requestId });
      else if (event.kind === 'pulloutOffered') this.mailQueue.push({ kind: 'pullout' });
      else if (event.kind === 'endingReached') this.showEnding();
    }
  }

  /** Once per frame: open the next queued envelope when the screen is free,
   * pausing on the first of a batch and restoring speed after the last. */
  private pumpMailQueue(): void {
    if (this.modal !== null || this.root.querySelector('.gazette-backdrop, .title-backdrop')) return;
    if (this.view.hud().gameOver) {
      this.mailQueue.length = 0;
      this.queuePriorSpeed = null;
      return;
    }
    const stillValid = (item: (typeof this.mailQueue)[number]): boolean => {
      if (item.kind === 'family') {
        const request = this.view.state.familyRequests.find((candidate) => candidate.id === item.requestId);
        return request?.status === 'pending';
      }
      if (item.kind === 'pullout') {
        return this.view.state.meta.era === 'pullout' && this.view.state.meta.ending === null;
      }
      return true;
    };
    while (this.mailQueue.length > 0 && !stillValid(this.mailQueue[0]!)) this.mailQueue.shift();
    if (this.mailQueue.length === 0) {
      if (this.queuePriorSpeed !== null) {
        const prior = this.queuePriorSpeed;
        this.queuePriorSpeed = null;
        this.bus.dispatch({ kind: 'setSpeed', speed: prior === 0 ? 1 : prior });
      }
      return;
    }
    if (this.queuePriorSpeed === null) {
      // Commands apply next frame, so this still reads the pre-pause speed.
      this.queuePriorSpeed = this.view.hud().speed as Speed;
      this.bus.dispatch({ kind: 'setSpeed', speed: 0 });
    }
    const item = this.mailQueue.shift()!;
    if (item.kind === 'letter') {
      this.bus.dispatch({ kind: 'markLetterRead', defId: item.defId });
      this.showLetter(item.defId, 'auto');
    } else if (item.kind === 'family') {
      this.showFamilyRequest(item.requestId, 'auto');
    } else {
      this.showPulloutModal();
    }
  }

  /**
   * The charter button is up from the first day so incorporation is never a
   * secret: greyed with a running tally while the checklist is short, pulsing
   * gold the moment it's complete, gone once the town has a name.
   */
  private updateCharterButton(hud: HudSnapshot): void {
    if (hud.townName !== null) {
      this.charterButton.style.display = 'none';
      return;
    }
    this.charterButton.style.display = '';
    const requirements = hud.charterRequirements;
    const met = requirements.filter((requirement) => requirement.met).length;
    this.charterButton.classList.toggle('locked', !hud.charterReady);
    if (hud.charterReady) {
      this.charterButton.textContent = 'Sign Charter';
      this.charterButton.title = 'The Territory will incorporate the camp — name it and sign.';
      return;
    }
    this.charterButton.textContent = `Sign Charter (${met}/${requirements.length})`;
    const unmet = requirements
      .filter((requirement) => !requirement.met)
      .map((requirement) =>
        requirement.progress === null ? `· ${requirement.label}` : `· ${requirement.label} — ${requirement.progress}`,
      );
    this.charterButton.title = `The Land Office still wants:\n${unmet.join('\n')}\n\nClick for details.`;
  }

  /** The locked charter button: the checklist, ticked off as far as it goes. */
  private showCharterRequirementsModal(): void {
    const hud = this.view.hud();
    if (hud.townName !== null) return;
    if (hud.charterReady) {
      this.showCharterModal();
      return;
    }
    this.closeModal();
    const rows = hud.charterRequirements.map((requirement) =>
      el(
        'div',
        { class: `charter-req${requirement.met ? ' met' : ''}` },
        el('span', { class: 'charter-req-mark' }, requirement.met ? '✓' : '·'),
        el(
          'span',
          {},
          el(
            'span',
            { class: 'charter-req-label' },
            requirement.progress === null ? requirement.label : `${requirement.label} — ${requirement.progress}`,
          ),
          ...(requirement.met ? [] : [el('span', { class: 'charter-req-hint' }, requirement.hint)]),
        ),
      ),
    );
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal letter' },
        el('div', { class: 'modal-title' }, 'REQUIREMENTS FOR INCORPORATION'),
        el('div', { class: 'mail-from' }, 'Territorial Land Office'),
        el(
          'p',
          {},
          'A camp becomes a town when it can look after its own. The Territory will consider a charter — ' +
            'a name of record, the right to levy taxes, and standing to attract residents — once all of the ' +
            'following hold at once:',
        ),
        el('div', { class: 'charter-reqs' }, ...rows),
        el('button', { class: 'modal-close', onclick: () => this.closeModal() }, 'Close'),
      ),
    );
    this.root.append(this.modal);
  }

  private showCharterModal(): void {
    if (!this.view.hud().charterReady) return;
    this.closeModal();
    const nameInput = el('input', { class: 'charter-input', type: 'text', placeholder: 'Name your town...' });
    nameInput.maxLength = 24;
    const sign = (): void => {
      const name = nameInput.value.trim();
      if (name.length === 0) {
        nameInput.classList.add('charter-input-error');
        return;
      }
      this.bus.dispatch({ kind: 'signCharter', name });
      this.closeModal();
    };
    nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') sign();
      event.stopPropagation();
    });
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal letter' },
        el('div', { class: 'modal-title' }, 'CHARTER OF INCORPORATION'),
        el('div', { class: 'mail-from' }, 'Territorial Land Office'),
        el(
          'p',
          {},
          'Whereas the settlement herein described maintains families in residence, a school, physician services, and a house of community; the Territory recognizes said settlement as an INCORPORATED TOWN, entitled to levy taxes, attract residents, and bear a name of record.',
        ),
        el('p', {}, 'Affix the name below and sign.'),
        nameInput,
        el('button', { class: 'modal-close', onclick: sign }, 'Sign the charter'),
        el('button', { class: 'modal-close secondary', onclick: () => this.closeModal() }, 'Not yet'),
      ),
    );
    this.root.append(this.modal);
    setTimeout(() => nameInput.focus(), 50);
  }

  private showPulloutModal(): void {
    const state = this.view.state;
    if (state.meta.era !== 'pullout' || state.meta.ending !== null) return;
    this.closeModal();
    // An ultimatum deserves the player's full attention. When opened by the
    // mail queue the session already paused; the topbar reopen path pauses here.
    const selfManaged = this.queuePriorSpeed === null;
    const priorSpeed = this.view.hud().speed as Speed;
    if (selfManaged) this.bus.dispatch({ kind: 'setSpeed', speed: 0 });
    const settlement = stripSettlementCents(this.view);
    const price = buyoutPriceCents(this.view);
    const weekly = Math.ceil(price / 10);
    const choose = (choice: 'strip' | 'buyout' | 'wait'): void => {
      this.bus.dispatch({ kind: 'resolvePullout', choice });
      this.closeModal();
      if (selfManaged) this.bus.dispatch({ kind: 'setSpeed', speed: priorSpeed === 0 ? 1 : priorSpeed });
    };
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal letter' },
        el('div', { class: 'modal-title' }, 'NOTICE OF SITE CLOSURE'),
        el('div', { class: 'mail-from' }, 'Consolidated Extraction Co. — Office of the Board'),
        el(
          'p',
          {},
          'The board has reviewed your shipment volumes. The ore is finished, and so is our interest. This site will be closed and its assets liquidated.',
        ),
        el('p', {}, 'Two courtesies are extended to site management, in recognition of service:'),
        el(
          'p',
          {},
          `ONE — a final settlement of ${formatMoney(settlement)} for orderly handover. Take it, walk away rich, and let the desert have the rest.`,
        ),
        el(
          'p',
          {},
          `TWO — purchase the entire site: every building, street, and stone, for ${formatMoney(price)}, payable over ten weeks at ${formatMoney(weekly)} per week. The board finds this option sentimental and expects you to fail.`,
        ),
        el('button', { class: 'modal-close', onclick: () => choose('buyout') }, `Buy the town — ${formatMoney(weekly)}/week for 10 weeks`),
        el('button', { class: 'modal-close secondary', onclick: () => choose('strip') }, `Take the settlement — ${formatMoney(settlement)}`),
        el('button', { class: 'modal-close secondary', onclick: () => choose('wait') }, 'Give me time to think'),
      ),
    );
    this.root.append(this.modal);
  }

  private showEnding(): void {
    if (this.epilogueShown && this.view.state.meta.ending) return;
    this.epilogueShown = true;
    this.closeModal();
    this.bus.dispatch({ kind: 'setSpeed', speed: 0 });
    showEpilogue(
      this.root,
      this.view,
      this.snapshot ? this.snapshot() : null,
      () => this.bus.dispatch({ kind: 'setSpeed', speed: 1 }),
      () => this.startNewGame(),
    );
  }

  private showToast(message: string): void {
    const toast = el('div', { class: 'toast' }, message);
    this.toastStack.append(toast);
    while (this.toastStack.children.length > 4) this.toastStack.firstElementChild?.remove();
    setTimeout(() => {
      toast.classList.add('fade');
      setTimeout(() => toast.remove(), 600);
    }, 4500);
  }

  private toggleMail(): void {
    if (this.mailOpen) {
      this.closeModal();
      return;
    }
    this.closeModal();
    this.mailOpen = true;
    const letters = [...this.view.state.letters.delivered].reverse();
    const list = el('div', { class: 'mail-list' });
    // Pending family requests come first — they need an answer.
    for (const request of this.view.state.familyRequests) {
      if (request.status !== 'pending') continue;
      const item = el(
        'button',
        {
          class: 'mail-item unread',
          onclick: () => this.showFamilyRequest(request.id),
        },
        el('span', { class: 'mail-subject' }, `${request.workerName} asks to bring their family`),
        el('span', { class: 'mail-from' }, 'NEEDS ANSWER'),
      );
      list.append(item);
    }
    if (letters.length === 0 && list.children.length === 0) list.append(el('div', { class: 'row' }, 'No mail yet.'));
    for (const letter of letters) {
      const def = letterDef(letter.defId);
      if (!def) continue;
      const item = el(
        'button',
        {
          class: `mail-item ${letter.read ? 'read' : 'unread'}`,
          onclick: () => {
            this.bus.dispatch({ kind: 'markLetterRead', defId: letter.defId });
            this.showLetter(letter.defId);
          },
        },
        el('span', { class: 'mail-subject' }, def.subject),
        el('span', { class: 'mail-from' }, def.from),
      );
      list.append(item);
    }
    this.modal = el(
      'div',
      { class: 'modal-backdrop', onclick: (event) => {
        if (event.target === this.modal) this.closeModal();
      } },
      el(
        'div',
        { class: 'panel modal' },
        el('div', { class: 'modal-title' }, 'Mail'),
        list,
        el('button', { class: 'modal-close', onclick: () => this.closeModal() }, 'Close'),
      ),
    );
    this.root.append(this.modal);
  }

  private showLetter(defId: string, source: 'mail' | 'auto' = 'mail'): void {
    const def = letterDef(defId);
    if (!def) return;
    this.closeModal();
    if (source === 'mail') this.mailOpen = true;
    const paragraphs = def.body(this.view).split('\n\n');
    // The charter offer can be signed straight from the envelope.
    const charterChain =
      defId === 'charter-offer' && this.view.hud().charterReady && this.view.state.meta.townName === null
        ? el('button', { class: 'modal-close', onclick: () => this.showCharterModal() }, 'Sign the charter')
        : null;
    const footer =
      source === 'auto'
        ? el('button', { class: `modal-close${charterChain ? ' secondary' : ''}`, onclick: () => this.closeModal() }, 'Close')
        : el('button', { class: `modal-close${charterChain ? ' secondary' : ''}`, onclick: () => this.toggleMailBack() }, 'Back to mail');
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal letter' },
        el('div', { class: 'modal-title' }, def.subject),
        el('div', { class: 'mail-from' }, `From: ${def.from}`),
        ...paragraphs.map((paragraph) => el('p', {}, paragraph)),
        ...(charterChain ? [charterChain] : []),
        footer,
      ),
    );
    this.root.append(this.modal);
  }

  private toggleMailBack(): void {
    this.closeModal();
    this.toggleMail();
  }

  private showFamilyRequest(requestId: number, source: 'mail' | 'auto' = 'mail'): void {
    const request = this.view.state.familyRequests.find((candidate) => candidate.id === requestId);
    if (!request || request.status !== 'pending') return;
    this.closeModal();
    if (source === 'mail') this.mailOpen = true;
    const firstName = request.workerName.split(' ')[0];
    const resolve = (approve: boolean): void => {
      this.bus.dispatch({ kind: 'resolveFamilyRequest', requestId, approve });
      this.closeModal();
    };
    const later = (): void => {
      if (source === 'mail') this.toggleMailBack();
      else this.closeModal();
    };
    // Mirror the sim's approve guard so the button can't silently fail.
    const ready = familySettlementReady(this.view);
    const approveButton = el(
      'button',
      { class: 'modal-close', onclick: () => resolve(true) },
      'Approve — welcome the family',
    );
    approveButton.disabled = !ready;
    const hint = ready
      ? null
      : el(
          'div',
          { class: 'mail-from' },
          vacantFamilyCapacity(this.view) === 0
            ? 'No vacant family home — build a cottage first. The request will wait in Mail.'
            : 'They need a working school and doctor first. The request will wait in Mail.',
        );
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal letter' },
        el('div', { class: 'modal-title' }, `${request.workerName} asks to bring their family`),
        el('div', { class: 'mail-from' }, `From: ${request.workerName}, Veteran`),
        el('p', {}, `Boss — I've given this camp my best years, and I mean to stay. But not alone.`),
        el(
          'p',
          {},
          `My family is ready to make the trip. They'll need a proper home — a cottage would do — and I won't bring my kids somewhere without a school and a doctor.`,
        ),
        el('p', {}, `Say yes and you'll never find a more loyal hand than me. — ${firstName}`),
        ...(hint ? [hint] : []),
        approveButton,
        el('button', { class: 'modal-close secondary', onclick: later }, 'Decide later'),
        el('button', { class: 'modal-close secondary', onclick: () => resolve(false) }, 'Decline for now'),
      ),
    );
    this.root.append(this.modal);
  }

  private showPayroll(breakdown: PayrollBreakdown): void {
    if (this.view.hud().gameOver) return;
    this.closeModal();
    const priorSpeed = this.view.hud().speed as Speed;
    this.bus.dispatch({ kind: 'setSpeed', speed: 0 });
    const row = (label: string, cents: number): HTMLElement =>
      el(
        'div',
        { class: `payrow ${cents < 0 ? 'neg' : cents > 0 ? 'pos' : ''}` },
        el('span', {}, label),
        el('span', {}, formatMoney(cents)),
      );
    const week = this.view.hud().week;
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal' },
        el('div', { class: 'modal-title' }, `Week ${week} settlement`),
        row('Wages', -breakdown.wages),
        breakdown.industryWages
          ? el(
              'div',
              { class: 'payrow' },
              el('span', { class: 'muted' }, '· of which industry crews'),
              el('span', { class: 'muted' }, formatMoney(-breakdown.industryWages)),
            )
          : el('span', {}),
        breakdown.rent ? row('Housing rent', breakdown.rent) : el('span', {}),
        breakdown.food ? row('Canteen & store', breakdown.food) : el('span', {}),
        breakdown.saloon ? row('Saloon', breakdown.saloon) : el('span', {}),
        breakdown.casino ? row('Casino', breakdown.casino) : el('span', {}),
        breakdown.fuel ? row('Generator fuel', -breakdown.fuel) : el('span', {}),
        breakdown.severance ? row('Severance (paid at dismissal)', -breakdown.severance) : el('span', {}),
        breakdown.oreSales ? row('Ore sold (banked earlier)', breakdown.oreSales) : el('span', {}),
        breakdown.goodsSales ? row('Goods sold (banked earlier)', breakdown.goodsSales) : el('span', {}),
        breakdown.town ? row('Town income', breakdown.town) : el('span', {}),
        breakdown.powerSales
          ? el(
              'div',
              { class: 'payrow' },
              el('span', { class: 'muted' }, '· of which power exports'),
              el('span', { class: 'muted' }, formatMoney(breakdown.powerSales)),
            )
          : el('span', {}),
        breakdown.passengerFares
          ? el(
              'div',
              { class: 'payrow' },
              el('span', { class: 'muted' }, '· of which passenger fares'),
              el('span', { class: 'muted' }, formatMoney(breakdown.passengerFares)),
            )
          : el('span', {}),
        breakdown.interest ? row('Debt interest', -breakdown.interest) : el('span', {}),
        el('hr', {}),
        row('Settlement net', breakdown.net),
        breakdown.oreSales + breakdown.goodsSales > 0
          ? row('Week total incl. banked sales', breakdown.net + breakdown.oreSales + breakdown.goodsSales)
          : el('span', {}),
        breakdown.missed
          ? el('div', { class: 'payrow warn' }, 'The books are in the red. The crew noticed.')
          : el('span', {}),
        this.view.hud().debtCents > 0
          ? el(
              'button',
              {
                class: 'modal-close secondary',
                onclick: () =>
                  this.showDebtModal(() =>
                    this.bus.dispatch({ kind: 'setSpeed', speed: priorSpeed === 0 ? 1 : priorSpeed }),
                  ),
              },
              'Pay down debt',
            )
          : el('span', {}),
        el(
          'button',
          {
            class: 'modal-close secondary',
            onclick: () =>
              this.showBooks(() => this.bus.dispatch({ kind: 'setSpeed', speed: priorSpeed === 0 ? 1 : priorSpeed })),
          },
          'Open the books',
        ),
        el(
          'button',
          {
            class: 'modal-close',
            onclick: () => {
              this.closeModal();
              this.bus.dispatch({ kind: 'setSpeed', speed: priorSpeed === 0 ? 1 : priorSpeed });
            },
          },
          'Back to work',
        ),
      ),
    );
    this.root.append(this.modal);
  }

  /** Pay the HQ loan down before the 5%/week interest eats the town. */
  private showDebtModal(onClose?: () => void): void {
    this.closeModal();
    const hud = this.view.hud();
    if (hud.debtCents <= 0) {
      onClose?.();
      return;
    }
    const close = (): void => {
      this.closeModal();
      onClose?.();
    };
    const pay = (amountCents: number): void => {
      this.bus.dispatch({ kind: 'repayDebt', amountCents });
      close();
    };
    const option = (label: string, amountCents: number): HTMLButtonElement => {
      const button = el('button', { class: 'modal-close', onclick: () => pay(amountCents) }, label);
      button.disabled = amountCents <= 0 || hud.cashCents < amountCents;
      return button;
    };
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal' },
        el('div', { class: 'modal-title' }, 'HQ loan'),
        el('div', { class: 'payrow' }, el('span', {}, 'Outstanding debt'), el('span', {}, formatMoney(hud.debtCents))),
        el('div', { class: 'payrow' }, el('span', {}, 'Cash on hand'), el('span', {}, formatMoney(hud.cashCents))),
        el('div', { class: 'payrow warn' }, 'HQ charges 5% interest every week it stands.'),
        option('Pay $500', 50_000),
        option('Pay half', Math.ceil(hud.debtCents / 2)),
        option(`Pay it all — ${formatMoney(hud.debtCents)}`, hud.debtCents),
        el('button', { class: 'modal-close secondary', onclick: close }, 'Not yet'),
      ),
    );
    this.root.append(this.modal);
  }

  /** The Books: the sim's transaction ledger, newest first. No running
   * balance — the ledger isn't a complete cash journal (interest accrues to
   * debt, the payroll net spans several entries), and a reconstructed
   * balance would lie. */
  private showBooks(onClose?: () => void): void {
    this.closeModal();
    const hud = this.view.hud();
    const entries = [...this.view.state.economy.ledger].slice(-60).reverse();
    const rows = entries.map((entry) =>
      el(
        'div',
        { class: `payrow ${entry.amountCents < 0 ? 'neg' : entry.amountCents > 0 ? 'pos' : ''}` },
        el('span', { class: 'muted' }, `W${Math.floor(entry.day / 7) + 1} ${DAY_NAMES[entry.day % 7]}`),
        el('span', {}, entry.note),
        el('span', {}, formatMoney(entry.amountCents)),
      ),
    );
    const close = (): void => {
      this.closeModal();
      onClose?.();
    };
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal' },
        el('div', { class: 'modal-title' }, 'The Books'),
        el('div', { class: 'payrow' }, el('span', {}, 'Cash on hand'), el('span', {}, formatMoney(hud.cashCents))),
        rows.length > 0
          ? el('div', { class: 'books-list' }, ...rows)
          : el('p', { class: 'muted' }, 'Nothing in the ledger yet.'),
        el('button', { class: 'modal-close', onclick: close }, 'Close the books'),
      ),
    );
    this.root.append(this.modal);
  }

  /** The music is CC0 — nothing is owed, which is why the thanks matter. */
  private showCredits(): void {
    this.closeModal();
    const rows = MUSIC_TRACKS.map((track) =>
      el(
        'div',
        { class: 'credit-row' },
        el('div', { class: 'credit-title' }, `“${track.title}”`),
        el('div', {}, `${track.role} · by `, el('b', {}, track.artist)),
        el(
          'a',
          { class: 'credit-link', href: track.source, target: '_blank', rel: 'noopener' },
          'hear more of their work →',
        ),
      ),
    );
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal letter' },
        el('div', { class: 'modal-title' }, 'MUSIC CREDITS'),
        el('div', { class: 'mail-from' }, 'The sounds of Boomtown'),
        el(
          'p',
          {},
          'These artists released their music to the public domain, free for anyone. Their generosity is the soul of this desert — go tell them so.',
        ),
        ...rows,
        el('p', {}, 'All other sounds are synthesized live by the game itself.'),
        el('button', { class: 'modal-close', onclick: () => this.closeModal() }, 'Close'),
      ),
    );
    this.root.append(this.modal);
  }

  private closeModal(): void {
    this.modal?.remove();
    this.modal = null;
    this.mailOpen = false;
  }

  private showGameOver(reason: string): void {
    this.closeModal();
    this.gameOverShown = true;
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal' },
        el('div', { class: 'modal-title' }, 'FORECLOSED'),
        el('div', { class: 'payrow' }, reason),
        el('button', { class: 'modal-close', onclick: () => this.startNewGame() }, 'Start over'),
      ),
    );
    this.root.append(this.modal);
  }

  private confirmNewGame(): void {
    this.closeModal();
    this.modal = el(
      'div',
      { class: 'modal-backdrop' },
      el(
        'div',
        { class: 'panel modal' },
        el('div', { class: 'modal-title' }, 'Abandon this site?'),
        el('div', { class: 'payrow' }, 'Your current town will be deleted and a fresh claim surveyed.'),
        el('button', { class: 'modal-close', onclick: () => this.startNewGame() }, 'Abandon and start fresh'),
        el('button', { class: 'modal-close secondary', onclick: () => this.closeModal() }, 'Keep playing'),
      ),
    );
    this.root.append(this.modal);
  }

  private startNewGame(): void {
    clearSave();
    const params = new URLSearchParams(location.search);
    params.set('new', '1');
    params.set('seed', String(Date.now() >>> 0));
    location.search = params.toString();
  }

  update(): void {
    const hud = this.view.hud();
    if (hud.gameOver && !this.gameOverShown) this.showGameOver(hud.gameOverReason ?? 'Game over');
    const quota = hud.quota;
    const goodsKey = hud.goods.map((entry) => `${entry.storedUnits}.${entry.priceCents}`).join(',');
    const charterKey = hud.charterRequirements.map((req) => `${req.met ? 1 : 0}${req.progress ?? ''}`).join(',');
    const cacheKey = `${charterKey}:${hud.cashCents}:${hud.debtCents}:${hud.day}:${hud.speed}:${hud.headcount}:${hud.employed}:${hud.adults}:${hud.depotIronDt}:${hud.depotGoldDt}:${hud.waterUnits}:${hud.ironPriceCents}:${hud.goldPriceCents}:${hud.unreadLetters}:${hud.pendingFamilyRequests}:${quota ? quota.deliveredDt + ':' + quota.completed + ':' + quota.failed : 'nq'}:${hud.townName}:${hud.era}:${hud.charterReady}:${hud.installments?.paidWeeks ?? -1}:${hud.endingTitle}:${goodsKey}:${hud.goodsCapacityUnits}:${hud.goodsTruckSelling}:${hud.freightOpen}`;
    if (cacheKey !== this.lastHudText) {
      this.lastHudText = cacheKey;
      this.townStat.textContent = hud.townName ?? '';
      this.townStat.style.display = hud.townName ? '' : 'none';
      this.updateCharterButton(hud);
      this.pulloutButton.style.display = hud.era === 'pullout' && !hud.endingTitle ? '' : 'none';
      this.cash.innerHTML = `cash <b>${formatMoney(hud.cashCents)}</b>${hud.debtCents > 0 ? ` <span class="debt-chip" title="Pay down the HQ loan">debt <b>${formatMoney(hud.debtCents)}</b></span>` : ''}`;
      this.cash.classList.toggle('negative', hud.cashCents < 0);
      this.date.innerHTML = `week <b>${hud.week + 1}</b> ${DAY_NAMES[hud.dayOfWeek]}`;
      this.crew.innerHTML = `pop <b>${hud.headcount}</b> · emp <b>${hud.employed}</b>/${hud.adults}`;
      this.depot.innerHTML = `depot <b>${formatTons(hud.depotIronDt)}</b> Fe · <b>${formatTons(hud.depotGoldDt)}</b> Au`;
      this.market.innerHTML = `mkt <b>${formatMoney(hud.ironPriceCents)}</b>/t Fe · <b>${formatMoney(hud.goldPriceCents)}</b>/t Au`;
      // Post-charter the goods economy exists: the two ore chips collapse
      // into the Market chip so the bar never gains net width.
      const marketMode = hud.townName !== null;
      this.depot.style.display = marketMode ? 'none' : '';
      this.market.style.display = marketMode ? 'none' : '';
      this.marketChip.style.display = marketMode ? '' : 'none';
      if (!marketMode && this.marketOpen) {
        this.marketOpen = false;
        this.lastMarketKey = '';
      }
      this.water.innerHTML = `water <b>${hud.waterUnits}</b>/${hud.waterCapacity}`;
      if (hud.installments) {
        this.quotaStat.innerHTML = `buyout <b>${hud.installments.paidWeeks}/${hud.installments.totalWeeks}</b> · ${formatMoney(hud.installments.weeklyCents)}/wk`;
      } else if (hud.era === 'independent' && !hud.endingTitle && hud.endureStreak > 0) {
        this.quotaStat.innerHTML = `endure <b>${hud.endureStreak}/12</b> wks`;
      } else if (hud.endingTitle) {
        this.quotaStat.innerHTML = `<b>${hud.endingTitle}</b>`;
      } else if (quota && !quota.completed && !quota.failed) {
        this.quotaStat.innerHTML = `quota <b>${formatTons(quota.deliveredDt)}</b>/${formatTons(quota.amountDt)} by wk ${quota.byWeek}`;
      } else if (quota?.failed) {
        this.quotaStat.innerHTML = `quota <b>MISSED</b>`;
      } else {
        this.quotaStat.textContent = '';
      }
      const mailCount = hud.unreadLetters + hud.pendingFamilyRequests;
      this.mailButton.textContent = mailCount > 0 ? `Mail (${mailCount})` : 'Mail';
      this.mailButton.classList.toggle('unread', mailCount > 0);
      this.sellButton.disabled = hud.depotIronDt + hud.depotGoldDt < 50;
      for (const [speed, button] of this.speedButtons) {
        button.classList.toggle('active', hud.speed === speed);
      }
      this.sidebar.refreshStates();
    }

    const hover = this.input.hover;
    if (hover) {
      const info = this.view.inspect(hover.x, hover.y);
      if (info) {
        this.inspectBox.style.display = '';
        this.inspectBox.replaceChildren(
          el('div', { class: 'title' }, info.title),
          ...info.lines.map((line) => el('div', {}, line)),
        );
      }
    } else {
      this.inspectBox.style.display = 'none';
    }

    // Live tile count + cost while dragging a paint tool; live placement
    // reason while a build ghost is on the map.
    const hoverKey = this.input.hover ? `${this.input.hover.x},${this.input.hover.y}` : '';
    const chipAffordable =
      this.input.erasing || this.input.pendingCostCents() <= this.view.state.economy.cashCents;
    const chipKey = `${this.input.tool}:${this.input.placeType}:${this.input.pendingVersion}:${this.input.lockedLevel}:${hoverKey}:${this.input.erasing ? 1 : 0}:${this.input.routeBlocked ? 1 : 0}:${chipAffordable ? 1 : 0}`;
    if (chipKey !== this.lastChipKey) {
      this.lastChipKey = chipKey;
      this.renderToolChip();
    }

    this.updateCrewPanel();
    this.updateSelectionPanel();
    this.updateMarketPanel();
    this.pumpMailQueue();
  }

  private updateMarketPanel(): void {
    const hud = this.view.hud();
    if (!this.marketOpen) {
      if (this.lastMarketKey !== '') {
        this.lastMarketKey = '';
        this.marketPanel.style.display = 'none';
      }
      return;
    }
    const goodsKey = hud.goods.map((entry) => `${entry.storedUnits}.${entry.priceCents}`).join(',');
    const key = `${hud.depotIronDt}:${hud.depotGoldDt}:${hud.ironPriceCents}:${hud.goldPriceCents}:${goodsKey}:${hud.goodsCapacityUnits}:${hud.goodsValueCents}:${hud.goodsTruckSelling}:${hud.freightOpen}:${hud.powerSurplusUnits}:${hud.powerPriceCents}`;
    if (key === this.lastMarketKey) return;
    this.lastMarketKey = key;
    this.marketPanel.style.display = '';

    const row = (label: string, stock: string, price: string): HTMLElement =>
      el(
        'div',
        { class: 'row needrow' },
        el('span', {}, label),
        el('span', {}, `${stock} · ${price}`),
      );
    const shipButton = el(
      'button',
      { class: 'mini wide', onclick: () => this.bus.dispatch({ kind: 'dispatchGoodsTruck' }) },
      hud.goodsTruckSelling ? 'wagon en route' : `Ship goods (${formatMoney(hud.goodsValueCents)})`,
    );
    shipButton.disabled =
      hud.goodsTruckSelling || !hud.freightOpen || hud.goodsValueCents <= BALANCE.goodsTruckFeeCents;
    const stored = hud.goods.reduce((sum, entry) => sum + entry.storedUnits, 0);
    this.marketPanel.replaceChildren(
      el('div', { class: 'title' }, 'Depot & market'),
      row('iron', `${formatTons(hud.depotIronDt)}`, `${formatMoney(hud.ironPriceCents)}/t`),
      row('gold', `${formatTons(hud.depotGoldDt)}`, `${formatMoney(hud.goldPriceCents)}/t`),
      ...hud.goods.map((entry) => row(entry.good, String(entry.storedUnits), formatMoney(entry.priceCents))),
      el(
        'div',
        { class: 'row needrow' },
        el('span', {}, 'goods depot'),
        el(
          'span',
          { class: hud.goodsCapacityUnits === 0 ? 'muted' : '' },
          hud.goodsCapacityUnits === 0 ? 'build a warehouse' : `${stored} / ${hud.goodsCapacityUnits}`,
        ),
      ),
      el('div', { class: 'row needrow' }, el('span', {}, 'freight'), el('span', { class: hud.freightOpen ? '' : 'muted' }, hud.freightOpen ? `auto-ships at ${formatMoney(BALANCE.goodsTruckAutoDepartValueCents)}` : 'build a freight office')),
      ...(hud.powerSurplusUnits > 0
        ? [
            el(
              'div',
              { class: 'row needrow' },
              el('span', {}, 'grid export'),
              el('span', {}, `${hud.powerSurplusUnits} · ${formatMoney(hud.powerSurplusUnits * hud.powerPriceCents)}/wk`),
            ),
          ]
        : []),
      shipButton,
    );
  }

  private updateCrewPanel(): void {
    const hud = this.view.hud();
    const bar = (value: number): string => `${Math.round(value * 100)}%`;
    const nowMs = performance.now();
    if (nowMs - this.paidIdleUpdatedMs >= 1000) {
      this.paidIdleUpdatedMs = nowMs;
      this.paidIdleDisplay = hud.paidIdle;
    }
    const key = [
      hud.morale.toFixed(2),
      hud.needsHousing.toFixed(2),
      hud.needsFood.toFixed(2),
      hud.needsWater,
      hud.needsDrink.toFixed(2),
      hud.needsGambling.toFixed(2),
      hud.drinkUnlocked,
      hud.gamblingUnlocked,
      hud.minersTarget,
      hud.haulersTarget,
      hud.minersActual,
      hud.haulersActual,
      hud.staffCount,
      hud.projectedWageCents,
      this.paidIdleDisplay,
      hud.employed,
      hud.hiringOpen,
      hud.headcount,
      hud.adults,
      hud.kids,
      hud.onPayroll,
      hud.industryOnPayroll,
      hud.dismissible,
      hud.tierCounts.join(','),
      hud.households,
      hud.familyEducation.toFixed(1),
      hud.familyHealth.toFixed(1),
      hud.familyCommunity.toFixed(1),
      hud.familyAir.toFixed(2),
      hud.attractiveness.toFixed(2),
      hud.era,
      hud.endureStreak,
      this.crewCollapsed,
    ].join(':');
    if (key === this.lastCrewKey) return;
    this.lastCrewKey = key;

    const needRow = (label: string, value: number, locked: boolean): HTMLElement =>
      el(
        'div',
        { class: 'row needrow' },
        el('span', {}, label),
        el('span', { class: locked ? 'muted' : value < 0.5 ? 'bad' : '' }, locked ? 'later' : bar(value)),
      );
    const counter = (
      label: string,
      actual: number,
      value: number,
      apply: (next: number) => void,
      anchor: string,
    ): HTMLElement =>
      el(
        'div',
        { class: 'row needrow' },
        el('span', { title: 'working now / target' }, label),
        el(
          'span',
          { title: 'working now / target' },
          `${actual} / `,
          el('button', { class: 'mini', onclick: () => apply(value - 1) }, '-'),
          ` ${value} `,
          el('button', { class: 'mini', 'data-tutorial': anchor, onclick: () => apply(value + 1) }, '+'),
        ),
      );
    // Downsizing: severance-paid dismissal of unassigned single workers.
    // The sim guards cash and toasts; the UI only greys out the obvious.
    const letGo = (count: number, label: string): HTMLButtonElement => {
      const button = el(
        'button',
        { class: 'mini', onclick: () => this.bus.dispatch({ kind: 'dismissWorkers', count }) },
        label,
      );
      button.disabled = hud.dismissible === 0;
      return button;
    };
    const dismissRow = el(
      'div',
      { class: 'row needrow' },
      el('span', {}, `idle singles ${hud.dismissible}`),
      el('span', {}, letGo(1, 'let go'), ' ', letGo(9999, 'all')),
    );

    const header = el(
      'div',
      { class: 'title crew-header' },
      el('span', {}, `Crew morale ${bar(hud.morale)}`),
      el(
        'button',
        {
          class: 'mini',
          onclick: () => {
            this.crewCollapsed = !this.crewCollapsed;
            this.lastCrewKey = '';
            this.updateCrewPanel();
          },
        },
        this.crewCollapsed ? '+' : '-',
      ),
    );
    if (this.crewCollapsed) {
      this.crewPanel.replaceChildren(header);
      return;
    }
    this.crewPanel.replaceChildren(
      header,
      needRow('housing', hud.needsHousing, false),
      needRow('food', hud.needsFood, false),
      needRow('water', hud.needsWater, false),
      needRow('drink', hud.needsDrink, !hud.drinkUnlocked),
      needRow('gambling', hud.needsGambling, !hud.gamblingUnlocked),
      el(
        'div',
        { class: 'row needrow' },
        el('span', {}, 'adults / kids'),
        el('span', {}, `${hud.adults} / ${hud.kids}`),
      ),
      el(
        'div',
        { class: 'row needrow', title: 'Waged crew + amenity staff, out of every adult in town.' },
        el('span', {}, 'employed'),
        el('span', {}, `${hud.employed} / ${hud.adults} adults`),
      ),
      counter(
        'miners',
        hud.minersActual,
        hud.minersTarget,
        (next) => this.bus.dispatch({ kind: 'setJobTargets', miners: Math.max(0, next), haulers: hud.haulersTarget }),
        'miners-plus',
      ),
      counter(
        'haulers',
        hud.haulersActual,
        hud.haulersTarget,
        (next) => this.bus.dispatch({ kind: 'setJobTargets', miners: hud.minersTarget, haulers: Math.max(0, next) }),
        'haulers-plus',
      ),
      ...(hud.industryOnPayroll > 0
        ? [
            el(
              'div',
              { class: 'row needrow', title: 'Farm and factory hands. Waged like miners.' },
              el('span', {}, 'industry hands'),
              el('span', {}, String(hud.industryOnPayroll)),
            ),
          ]
        : []),
      ...(hud.staffCount > 0
        ? [
            el(
              'div',
              { class: 'row needrow', title: 'Amenity staff are unpaid — they live off the takings.' },
              el('span', {}, 'amenity staff'),
              el('span', {}, String(hud.staffCount)),
            ),
          ]
        : []),
      el(
        'div',
        { class: 'row needrow', title: 'Waged crew draw full pay even while idle.' },
        el('span', {}, 'next payday'),
        el('span', {}, formatMoney(-hud.projectedWageCents)),
      ),
      ...(this.paidIdleDisplay > 0
        ? [
            el(
              'div',
              {
                class: 'row needrow',
                title:
                  'Waged workers with no task right now. They still draw full pay — lower the targets or dismiss idle singles.',
              },
              el('span', {}, 'paid but idle'),
              el(
                'span',
                { class: this.paidIdleDisplay * 3 > hud.onPayroll ? 'bad' : '' },
                String(this.paidIdleDisplay),
              ),
            ),
          ]
        : []),
      el(
        'div',
        { class: 'row needrow' },
        el('span', {}, 'tiers T/R/V/S'),
        el('span', {}, hud.tierCounts.join(' / ')),
      ),
      ...(hud.households > 0 || hud.tierCounts[2] > 0
        ? [
            el('div', { class: 'row needrow' }, el('span', {}, 'families'), el('span', {}, String(hud.households))),
          ]
        : []),
      ...(hud.households > 0
        ? [
            needRow('school', hud.familyEducation, false),
            needRow('health', hud.familyHealth, false),
            needRow('community', hud.familyCommunity, false),
            needRow('clean air', hud.familyAir, false),
          ]
        : []),
      ...(hud.townName
        ? [needRow('attractive', hud.attractiveness, false)]
        : []),
      ...(hud.era === 'independent' && !hud.endingTitle
        ? [
            el(
              'div',
              { class: 'row needrow' },
              el('span', {}, 'endure'),
              el('span', { class: hud.endureStreak > 0 ? '' : 'muted' }, `${hud.endureStreak} / 12 wks`),
            ),
          ]
        : []),
      el(
        'div',
        { class: 'row needrow' },
        el('span', {}, 'hiring'),
        el(
          'button',
          {
            class: 'mini wide',
            'data-tutorial': 'hiring-toggle',
            onclick: () => this.bus.dispatch({ kind: 'setHiring', open: !hud.hiringOpen }),
          },
          hud.hiringOpen ? 'OPEN' : 'closed',
        ),
      ),
      dismissRow,
    );
  }

  private updateSelectionPanel(): void {
    const id = this.input.selectedBuildingId;
    const building = id === null ? null : this.view.state.buildings.find((candidate) => candidate.id === id);
    if (!building) {
      if (this.lastSelectionKey !== '') {
        this.lastSelectionKey = '';
        this.selectionPanel.style.display = 'none';
      }
      return;
    }
    const def = BUILDING_DEFS[building.type];
    const stall = def.produces ? productionStall(this.view.state, building) : null;
    const key = `${building.id}:${building.staffAssigned.length}:${building.powered}:${building.watered}:${building.functional}:${building.powerEff.toFixed(2)}:${building.inputUnits}:${building.outputUnits}:${stall}`;
    if (key === this.lastSelectionKey) return;
    this.lastSelectionKey = key;
    this.selectionPanel.style.display = '';

    const rows: HTMLElement[] = [
      el('div', { class: 'title' }, def.label),
      el('div', { class: 'row' }, building.functional ? 'Operating' : 'NOT OPERATING'),
    ];
    if (def.produces) {
      rows.push(el('div', { class: 'row' }, `Output ${def.produces.unitsPerDay} ${def.produces.good}/day`));
      rows.push(
        el(
          'div',
          { class: 'row' },
          `Store ${building.outputUnits}/${outputCapUnits(building.type)} ${def.produces.good} awaiting pickup`,
        ),
      );
      if (stall === `no ${def.consumes?.good}`) {
        rows.push(el('div', { class: 'row' }, `NO FEED - haulers must deliver ${def.consumes!.good}`));
      } else if (stall === 'output full') {
        rows.push(el('div', { class: 'row' }, 'OUTPUT FULL - store and ground piles are backed up; haulers are behind'));
      }
    }
    if (def.consumes) {
      rows.push(
        el('div', { class: 'row' }, `Feed ${building.inputUnits}/${def.consumes.bufferUnits} ${def.consumes.good}`),
      );
    }
    if (def.powerDraw > 0) {
      rows.push(
        el(
          'div',
          { class: 'row' },
          building.powered
            ? building.powerEff < 1
              ? `Brownout (${Math.round(building.powerEff * 100)}% power)`
              : 'Powered'
            : 'NO POWER - route poles from a generator',
        ),
      );
    }
    if (def.waterPerDay !== undefined || building.type === 'well') {
      rows.push(el('div', { class: 'row' }, building.watered ? 'Water connected' : 'NO WATER - pipe to a tank'));
    }
    if (def.staffRequired > 0) {
      rows.push(
        el(
          'div',
          { class: 'row needrow' },
          el('span', {}, `staff ${building.staffAssigned.length}/${def.staffRequired}`),
          el(
            'span',
            {},
            el('button', { class: 'mini', onclick: () => this.bus.dispatch({ kind: 'adjustStaff', buildingId: building.id, delta: -1 }) }, '-'),
            ' ',
            el('button', { class: 'mini', 'data-tutorial': 'staff-plus', onclick: () => this.bus.dispatch({ kind: 'adjustStaff', buildingId: building.id, delta: 1 }) }, '+'),
          ),
        ),
      );
    }
    this.selectionPanel.replaceChildren(...rows);
  }

  /** Slim status chip: only visible while a tool is active or a level is locked. */
  private renderToolChip(): void {
    const tool = this.input.tool;
    const lock = this.input.lockedLevel;
    if (tool === 'idle' && lock === null) {
      this.toolChip.style.display = 'none';
      return;
    }
    const toolLabel =
      tool === 'place' && this.input.placeType
        ? `BUILD ${BUILDING_DEFS[this.input.placeType].label.toUpperCase()}`
        : tool === 'dig'
          ? 'DIG'
          : tool === 'cancelDig'
            ? 'CANCEL DIG'
            : tool === 'road'
              ? 'ROAD'
              : tool === 'pipe'
                ? 'PIPE'
                : tool === 'cable'
                  ? 'BURIED CABLE'
                  : tool === 'paved'
                    ? 'PAVE STREET'
                    : tool === 'reclaim'
                      ? 'RECLAIM LAND'
                      : tool === 'demolish'
                        ? 'DEMOLISH'
                        : null;
    const isRoute = tool === 'road' || tool === 'pipe' || tool === 'cable' || tool === 'paved';
    const parts: string[] = [];
    if (toolLabel) parts.push(`${toolLabel} · Q cancels`);
    if (isRoute) parts.push('drag routes · shift paints · right-drag removes');
    // Why the ghost is red — otherwise "corridor blocked" is invisible.
    if (tool === 'place' && this.input.placeType && this.input.hover) {
      const check = this.view.canPlace(this.input.placeType, this.input.hover.x, this.input.hover.y);
      if (!check.ok && check.reason) parts.push(check.reason.toUpperCase());
    }
    let unaffordable = false;
    if (this.input.routeBlocked) {
      parts.push('NO ROUTE');
    } else if (this.input.erasing && isRoute) {
      const refund = this.input.pendingRefundCents();
      if (refund > 0) parts.push(`${this.input.pendingCols.size} tiles · salvage ${formatMoney(refund)}`);
    } else if (this.input.pendingCols.size > 0) {
      const cost = this.input.pendingCostCents();
      if (cost > 0) {
        const tiles = isRoute ? this.input.pendingNewTileCount() : this.input.pendingCols.size;
        parts.push(`${tiles} tiles · ${formatMoney(cost)}`);
        if (cost > this.view.state.economy.cashCents) {
          unaffordable = true;
          parts.push("CAN'T AFFORD");
        }
      }
    }
    if (lock !== null) parts.push(`level ${lock === 0 ? 'surface' : `-${lock}`} · [ ] change · 0 auto`);
    this.toolChip.classList.toggle('warn', unaffordable || this.input.routeBlocked);
    this.toolChip.textContent = parts.join('   ');
    this.toolChip.style.display = '';
  }
}
